import { Navigate, Route, Routes, useLocation } from "react-router-dom";

import { Shell } from "./components/Shell";
import { useApp } from "./context/AppContext";
import { btpModule } from "./modules/btp/module";
import { AccountsPage } from "./modules/finance/AccountsPage";
import { FormationLayout } from "./modules/formation/FormationLayout";
import { FormationFeatureGate } from "./modules/formation/access";
import { FormationAccountsPage } from "./modules/formation/pages/AccountsPage";
import { FormationCatalogPage } from "./modules/formation/pages/CatalogPage";
import { FormationClosuresPage } from "./modules/formation/pages/ClosuresPage";
import { FormationDashboardPage } from "./modules/formation/pages/DashboardPage";
import { FormationEnrollmentsPage } from "./modules/formation/pages/EnrollmentsPage";
import { FormationExpensesPage } from "./modules/formation/pages/ExpensesPage";
import { FormationJournalPage } from "./modules/formation/pages/JournalPage";
import { FormationLearnersPage } from "./modules/formation/pages/LearnersPage";
import { FormationPaymentsPage } from "./modules/formation/pages/PaymentsPage";
import { FormationReportsPage } from "./modules/formation/pages/ReportsPage";
import { FormationSessionsPage } from "./modules/formation/pages/SessionsPage";
import { TiersDetailPage } from "./modules/tiers/TiersDetailPage";
import { TiersPage } from "./modules/tiers/TiersPage";
import { ActivatePage } from "./pages/ActivatePage";
import { DashboardPage } from "./pages/DashboardPage";
import { LoginPage } from "./pages/LoginPage";
import { CommercialServicesPage } from "./pages/CommercialServicesPage";
import { LinkAccountPage } from "./pages/LinkAccountPage";
import { JoinPage } from "./pages/JoinPage";
import { CreateBusinessPage } from "./pages/CreateBusinessPage";
import { EnterprisesPage } from "./pages/EnterprisesPage";
import { ProjectDetailPage } from "./pages/ProjectDetailPage";
import { ProjectsPage } from "./pages/ProjectsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { TemplateBuilderPage } from "./pages/TemplateBuilderPage";
import { TemplatesPage } from "./pages/TemplatesPage";
import { TransportLayout } from "./modules/transport/common";
import { TransportNetwork } from "./modules/transport/Network";
import { TransportDepartures, TransportHome } from "./modules/transport/Departures";
import { TransportCounter } from "./modules/transport/Counter";
import { TransportBoarding } from "./modules/transport/Boarding";
import { TransportReports } from "./modules/transport/Reports";
import { TransportProfiles } from "./modules/transport/Profiles";
import { PublicBooking } from "./modules/transport/PublicBooking";
import { OfflineTerminal } from "./modules/transport/OfflineTerminal";

export function App() {
  const { authenticated, loading, bootstrap } = useApp();
  const location = useLocation();

  if (location.pathname.startsWith("/transport/reserver/")) return <Routes><Route path="/transport/reserver/:company" element={<PublicBooking />} /></Routes>;
  if (location.pathname.startsWith("/transport/terminal/")) return <Routes><Route path="/transport/terminal/:departureId" element={<OfflineTerminal />} /></Routes>;

  if (location.pathname === "/join") {
    return <Routes><Route path="/join" element={<JoinPage />} /></Routes>;
  }

  if (location.pathname === "/link-account") return <LinkAccountPage />;

  if (location.pathname === "/activate") {
    return <Routes><Route path="/activate" element={<ActivatePage />} /></Routes>;
  }
  if (loading) return <div className="grid min-h-screen place-items-center bg-slate-950 text-sm font-semibold text-white">Chargement de SahelTech Platform…</div>;
  if (!authenticated) return <LoginPage />;
  if (location.pathname.startsWith("/services-commerciaux")) {
    return <Routes>
      <Route path="/services-commerciaux" element={<CommercialServicesPage />} />
      <Route path="/services-commerciaux/:code" element={<CommercialServicesPage />} />
      <Route path="/services-commerciaux/:code/:section" element={<CommercialServicesPage />} />
    </Routes>;
  }
  if (location.pathname === "/create-business") return bootstrap?.identity_federated ? <CreateBusinessPage /> : <Navigate to="/entreprises" replace />;
  if (location.pathname === "/entreprises") return <EnterprisesPage />;
  if (bootstrap?.organisations?.length === 0 && bootstrap?.identity_federated) return <CreateBusinessPage />;
  const btpEnabled = (bootstrap?.navigation || []).some((item) => item.code.toUpperCase() === "BTP")
    && (!bootstrap?.membership?.btp || bootstrap.membership.btp.capabilities.length > 0);
  const transport = bootstrap?.membership?.transport;
  const transportEnabled = Boolean(transport?.capabilities.length && (transport.active || transport.continuity));

  return <Routes>
    <Route element={<Shell />}>
      <Route index element={<DashboardPage />} />
      <Route path="settings" element={<SettingsPage />} />
      <Route path="tiers" element={<TiersPage />} />
      <Route path="tiers/:tiersId" element={<TiersDetailPage />} />
      <Route path="organisations" element={<Navigate to="/settings?section=company" replace />} />
      <Route path="projects" element={<ProjectsPage />} />
      <Route path="projects/:projectId" element={<ProjectDetailPage />} />
      <Route path="templates" element={<TemplatesPage />} />
      <Route path="templates/:versionId/builder" element={<TemplateBuilderPage />} />
      <Route path="team" element={<Navigate to="/settings?section=team" replace />} />
      <Route path="modules" element={<Navigate to="/settings?section=modules" replace />} />
      <Route path="integrations" element={<Navigate to="/settings?section=integrations" replace />} />
      {btpEnabled && btpModule.routes.map((route) => <Route key={route.path} path={route.path} element={route.element} />)}
      {btpEnabled && <Route path="finance/comptes" element={<AccountsPage />} />}
      {transportEnabled && <Route path="transport" element={<TransportLayout />}>
        <Route index element={<TransportHome />} />
        <Route path="reseau" element={<TransportNetwork />} />
        <Route path="departs" element={<TransportDepartures />} />
        <Route path="guichet" element={<TransportCounter />} />
        <Route path="embarquement" element={<TransportBoarding />} />
        <Route path="rapports" element={<TransportReports />} />
        <Route path="profils" element={<TransportProfiles />} />
      </Route>}
      <Route path="formation" element={<FormationLayout />}>
        <Route index element={<FormationFeatureGate code="formation.dashboard.view"><FormationDashboardPage /></FormationFeatureGate>} />
        <Route path="formations" element={<FormationFeatureGate code="formation.catalog.view"><FormationCatalogPage /></FormationFeatureGate>} />
        <Route path="sessions" element={<FormationFeatureGate code="formation.sessions.view"><FormationSessionsPage /></FormationFeatureGate>} />
        <Route path="apprenants" element={<FormationFeatureGate code="formation.learners.view"><FormationLearnersPage /></FormationFeatureGate>} />
        <Route path="inscriptions" element={<FormationFeatureGate code="formation.enrollments.view"><FormationEnrollmentsPage /></FormationFeatureGate>} />
        <Route path="paiements" element={<FormationFeatureGate code="formation.payments.view"><FormationPaymentsPage /></FormationFeatureGate>} />
        <Route path="depenses" element={<FormationFeatureGate code="formation.expenses.view"><FormationExpensesPage /></FormationFeatureGate>} />
        <Route path="comptes" element={<FormationFeatureGate code="formation.accounts.view"><FormationAccountsPage /></FormationFeatureGate>} />
        <Route path="journal" element={<FormationFeatureGate code="formation.journal.view"><FormationJournalPage /></FormationFeatureGate>} />
        <Route path="clotures" element={<FormationFeatureGate code="formation.closures.view"><FormationClosuresPage /></FormationFeatureGate>} />
        <Route path="rapports" element={<FormationFeatureGate code="formation.reports.cashflow"><FormationReportsPage /></FormationFeatureGate>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Route>
  </Routes>;
}
